import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Component, DestroyRef, inject, OnInit, signal, TemplateRef, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  CommonTableColumn,
  CommonTableComponent,
  CommonTableConfig,
} from '../../components/common-table/common-table.component';
import { DateRange } from '../../components/date-range-picker/date-range-picker.component';
import { StatusCellComponent } from '../../components/common-table/status-cell/status-cell.component';
import { CurrencyCellComponent } from '../../components/common-table/currency-cell/currency-cell.component';
import { UserInfoCellComponent } from '../../components/common-table/user-info-cell/user-info-cell.component';
import { DateTimeCellComponent } from '../../components/common-table/datetime-cell/datetime-cell.component';
import { NumberCellComponent } from '../../components/common-table/number-cell/number-cell.component';
import { PayoutRequestDetailComponent } from '../payout-request-detail/payout-request-detail.component';
import {
  AdminService,
  AdminPayoutsControllerGetAllPayouts200Response,
  AdminPayoutsControllerGetPayoutKpi200Response,
  PayoutRequestDto,
} from '@smatchy/sdk';
import { map } from 'rxjs/operators';
import { ToastComponent } from '../../components/shared-ui/success-toast/success-toast.component';
import { formatDateISO } from '../../utils/date.util';
import {
  PayoutRequestKpiListComponent,
  PayoutRequestKpiItem,
} from '../../components/payout-request-kpi-list/payout-request-kpi-list.component';

export type PayoutRequestStatus =
  | 'pending'
  | 'approved'
  | 'in_transit'
  | 'transferred'
  | 'declined'
  | 'failed'
  | 'canceled'
  | 'waiting-approval';
interface PayoutRequestData {
  payoutId: string;
  organizer: {
    name: string;
    email: string;
    avatarUrl?: string;
  };
  payoutAmount: number;
  eventRequestCount: number;
  requestedDate: Date | null;
  status?: PayoutRequestStatus;
}
@Component({
  selector: 'bo-payout-request',
  templateUrl: 'payout-request.component.html',
  standalone: true,
  imports: [TranslateModule, 
    CommonTableComponent,
    StatusCellComponent,
    DateTimeCellComponent,
    NumberCellComponent,
    UserInfoCellComponent,
    CurrencyCellComponent,
    PayoutRequestDetailComponent,
    ToastComponent,
    PayoutRequestKpiListComponent,
  ],
})
export class PayoutRequestComponent implements OnInit {
  #adminService = inject(AdminService);
  #translate = inject(TranslateService);
  #destroyRef = inject(DestroyRef);
  searchValue = signal('');
  dateRange = signal<DateRange>({ startDate: null, endDate: null });
  loading = signal(false);
  data = signal<PayoutRequestData[]>([]);
  totalRecords = signal<number | undefined>(undefined);
  // Held so the KPI cards can be rebuilt in the new language on a toggle.
  #kpiValues = signal<{ pending: number | null; processing: number | null }>({ pending: null, processing: null });
  kpis = signal<PayoutRequestKpiItem[]>([]);

  private t(key: string): string {
    return this.#translate.instant(key);
  }

  private buildKpis(): PayoutRequestKpiItem[] {
    const values = this.#kpiValues();
    return [
      {
        title: this.t('financePages.payoutKpiAwaitingTitle'),
        value: values.pending,
        subtitle: this.t('financePages.payoutKpiAwaitingSubtitle'),
        valueColor: '#14B8A6',
      },
      {
        title: this.t('financePages.payoutKpiProcessingTitle'),
        value: values.processing,
        subtitle: this.t('financePages.payoutKpiProcessingSubtitle'),
        valueColor: '#2563EB',
      },
    ];
  }

  private buildColumns(): CommonTableColumn[] {
    return [
      { label: this.t('financePages.payoutColId'), field: 'id', sortable: true, width: '15%' },
      { label: this.t('financePages.payoutColOrganizer'), field: 'organizer', sortable: true, width: '12%', align: 'left' },
      { label: this.t('financePages.payoutColAmount'), field: 'payoutAmount', sortable: true, width: '20%', align: 'right' },
      { label: this.t('financePages.payoutColEvents'), field: 'eventRequestCount', sortable: true, width: '20%', align: 'right' },
      { label: this.t('financePages.payoutColRequestedDate'), field: 'requestedDate', sortable: true, width: '20%', align: 'right' },
      { label: this.t('financePages.payoutColStatus'), field: 'status', sortable: true, width: '15%', minWidth: '110px', align: 'right' },
    ];
  }

  private applyTranslations(): void {
    this.columns = this.buildColumns();
    this.tableConfig = {
      ...this.tableConfig,
      toolbar: { ...this.tableConfig.toolbar, globalFilterPlaceholder: this.t('financePages.searchOrganizer') },
    };
    this.kpis.set(this.buildKpis());
  }
  private searchDebounceTimeout: ReturnType<typeof setTimeout> | null = null;
  private sortBy: 'payoutId' | 'organizer' | 'payoutAmount' | 'requestedDate' | 'payoutStatus' | undefined;
  private sortOrder: 'asc' | 'desc' | undefined;
  private page = 1;
  private limit = 10;

  @ViewChild('bodyTemplate', { static: true })
  bodyTemplate!: TemplateRef<{ $implicit: PayoutRequestData; rowIndex: number }>;
  @ViewChild(ToastComponent) toastComponent?: ToastComponent;

  dialogVisible = signal(false);
  selectedPayoutId = signal<string | null>(null);
  toastVisible = signal(false);
  toastTitle = signal('');
  toastMessage = signal('');
  toastIcon = signal('pi pi-check');

  columns: CommonTableColumn[] = [];

  tableConfig: CommonTableConfig = {
    table: {
      showBorder: false,
    },
    toolbar: {
      globalFilterPlaceholder: '',
      globalFilterFields: ['name', 'email'],
      showDateRangePicker: true,
    },
    filters: [],
  };

  onRowClick(item: PayoutRequestData) {
    this.selectedPayoutId.set(item.payoutId);
    this.dialogVisible.set(false);
  }

  onDialogVisibleChange(visible: boolean): void {
    this.dialogVisible.set(visible);
    if (!visible) {
      this.selectedPayoutId.set(null);
    }
  }

  onApprove(payoutId: string): void {
    this.#adminService.approvePayout({ id: payoutId }).subscribe({
      next: (response) => {
        if (response.error) {
          this.showErrorToast(this.t('financePages.payoutApproveFailedTitle'), this.t('financePages.payoutApproveFailedMsg'));
          return;
        }
        this.loadPendingPayouts();
        this.loadPayoutKpi();
        this.dialogVisible.set(false);
        this.showSuccessToast(this.t('financePages.payoutApprovedTitle'), this.t('financePages.payoutApprovedMsg'));
      },
      error: (error) => {
        console.error('Error approving payout:', error);
        this.showErrorToast(this.t('financePages.payoutApproveFailedTitle'), this.t('financePages.payoutApproveFailedMsg'));
      },
    });
  }

  onDecline(data: { payoutId: string; reason: string }): void {
    this.#adminService
      .rejectPayout({
        id: data.payoutId,
        rejectPayoutDto: { reason: data.reason },
      })
      .subscribe({
        next: (response) => {
          if (response.error) {
            this.showErrorToast(this.t('financePages.payoutDeclineFailedTitle'), this.t('financePages.payoutDeclineFailedMsg'));
            return;
          }
          this.loadPendingPayouts();
          this.loadPayoutKpi();
          this.dialogVisible.set(false);
          this.showSuccessToast(this.t('financePages.payoutDeclinedTitle'), this.t('financePages.payoutDeclinedMsg'));
        },
        error: (error) => {
          console.error('Error rejecting payout:', error);
          this.showErrorToast(this.t('financePages.payoutDeclineFailedTitle'), this.t('financePages.payoutDeclineFailedMsg'));
        },
      });
  }

  onSearchChange(value: string): void {
    this.searchValue.set(value);
    this.page = 1;
    if (this.searchDebounceTimeout) {
      clearTimeout(this.searchDebounceTimeout);
    }
    this.searchDebounceTimeout = setTimeout(() => {
      this.loadPendingPayouts();
    }, 300);
  }

  onDateRangeChange(range: DateRange): void {
    this.dateRange.set(range);
    this.page = 1;
    this.loadPendingPayouts();
  }

  onSortChange(event: { sortBy?: string; sortOrder?: 'asc' | 'desc' }): void {
    const newSortBy = event.sortBy as
      | 'payoutId'
      | 'organizer'
      | 'payoutAmount'
      | 'requestedDate'
      | 'payoutStatus'
      | undefined;

    const newSortOrder = event.sortOrder;

    if (this.sortBy === newSortBy && this.sortOrder === newSortOrder) {
      return;
    }

    this.sortBy = newSortBy;
    this.sortOrder = newSortOrder;
    this.loadPendingPayouts();
  }

  mapColumnToSortBy(field: string): string | undefined {
    switch (field) {
      case 'id':
        return 'payoutId';
      case 'organizer':
        return 'organizer';
      case 'payoutAmount':
        return 'payoutAmount';
      case 'eventRequestCount':
        return undefined;
      case 'requestedDate':
        return 'requestedDate';
      case 'status':
        return 'payoutStatus';
      default:
        return undefined;
    }
  }

  onClearFilters(): void {
    this.searchValue.set('');
    this.dateRange.set({ startDate: null, endDate: null });
    this.page = 1;
    this.loadPendingPayouts();
  }

  onPageChange(event: { page: number; limit: number }): void {
    if (this.page === event.page && this.limit === event.limit) {
      return;
    }
    this.page = event.page;
    this.limit = event.limit;
    this.loadPendingPayouts();
  }

  loadPendingPayouts(): void {
    this.loading.set(true);
    const search = this.searchValue().trim() || '';
    const currentRange = this.dateRange();

    const requestedStartDate = currentRange.startDate ? formatDateISO(currentRange.startDate) : undefined;
    const requestedEndDate = currentRange.endDate ? formatDateISO(currentRange.endDate) : undefined;

    const backendSortBy = this.mapColumnToSortBy(this.sortBy || '');
    this.#adminService
      .getAllPayouts({
        search,
        startDate: requestedStartDate,
        endDate: requestedEndDate,
        sortBy: backendSortBy as 'requestedAt' | 'amount' | 'status' | undefined,
        sortOrder: this.sortOrder,
        page: this.page,
        limit: this.limit,
      })
      .pipe(
        map((response: AdminPayoutsControllerGetAllPayouts200Response) => {
          if (response.error) {
            console.error('Failed to load pending payouts:', response.error);
            return [];
          }
          if (!response.value?.payouts) {
            return [];
          }
          if (typeof response.value.total === 'number') {
            this.totalRecords.set(response.value.total);
          } else {
            this.totalRecords.set(undefined);
          }

          return response.value.payouts.map((payout: PayoutRequestDto) => {
            const backendStatus = payout.status;

            let uiStatus: PayoutRequestData['status'];
            if (backendStatus === 'pending') {
              uiStatus = 'waiting-approval';
            } else if (backendStatus === 'paid') {
              uiStatus = 'transferred';
            } else {
              uiStatus = backendStatus;
            }

            return {
              payoutId: payout.id,
              organizer: {
                name: payout.organizerName,
                email: payout.organizerEmail,
                avatarUrl: payout?.organizerAvatarUrl,
              },
              payoutAmount: payout.amount,
              eventRequestCount: payout.events?.length ?? 0,
              requestedDate: payout.requestedAt ? new Date(payout.requestedAt) : null,
              status: uiStatus,
            };
          });
        }),
      )
      .subscribe({
        next: (payouts) => {
          this.data.set(payouts);
          this.loading.set(false);
        },
        error: (error) => {
          console.error('Error loading pending payouts:', error);
          this.data.set([]);
          this.loading.set(false);
        },
      });
  }

  showSuccessToast(title: string, message: string): void {
    this.toastTitle.set(title);
    this.toastMessage.set(message);
    this.toastIcon.set('pi pi-check');
    this.toastComponent?.show();
  }

  showErrorToast(title: string, message: string): void {
    this.toastTitle.set(title);
    this.toastMessage.set(message);
    this.toastIcon.set('pi pi-times-circle');
    this.toastComponent?.show();
  }

  loadPayoutKpi(): void {
    this.#adminService
      .getPayoutKpi()
      .pipe(
        map((response: AdminPayoutsControllerGetPayoutKpi200Response) => {
          if (response?.error) {
            console.error('Failed to load Withdrawal KPI:', response.error);
            return null;
          }
          return response?.value ?? null;
        }),
      )
      .subscribe({
        next: (data) => {
          if (data) {
            this.#kpiValues.set({ pending: data.totalPendingAmount, processing: data.totalApprovedAmount });
            this.kpis.set(this.buildKpis());
          }
        },
        error: (error) => {
          console.error('Error loading Withdrawal KPI:', error);
        },
      });
  }

  ngOnInit(): void {
    this.applyTranslations();
    this.#translate.onLangChange.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe(() => this.applyTranslations());
    this.loadPendingPayouts();
    this.loadPayoutKpi();
  }
}
